import { aCentimos } from "@/componentes/tesoreria/dinero"
import { normalizarDecimal } from "@/lib/decimales"

/**
 * Las cuentas de las líneas de una orden, en ENTEROS.
 *
 * ⚠ **La pantalla tiene que dar el mismo céntimo que el servidor.** El subtotal que
 * se ve al redactar es el que luego guarda `ordenes_pago.py`: `cantidad × precio`
 * redondeado a céntimos, media unidad hacia arriba (`ROUND_HALF_UP`, ver
 * `services/fiscal.py::redondear`). En coma flotante `2.5 × 25.51` no da lo mismo, y
 * una orden que en pantalla suma 351,00 y en la base 350,99 es una orden que alguien
 * aprueba mirando otra cifra.
 *
 * La cantidad admite seis decimales —es `TASA`, `Numeric(18, 6)`— y el precio dos
 * —es `DINERO`—: los mismos topes que la base, para que nada se redondee al guardar.
 */

// La coma y el punto valen los dos como decimal; las reglas viven en un solo sitio.
export { normalizarDecimal } from "@/lib/decimales"

/** Cuántos decimales trae un número ya normalizado. */
function decimales(texto: string): number {
  return texto.split(".")[1]?.length ?? 0
}

/** La cantidad, válida si es mayor que cero y con hasta seis decimales. */
export function cantidadValida(texto: string): string | null {
  const n = normalizarDecimal(texto)
  if (n === null || decimales(n) > 6 || aMillonesimas(n) === 0n) return null
  return n
}

/** El precio, válido si es mayor que cero y con hasta dos decimales. */
export function precioValido(texto: string): string | null {
  const n = normalizarDecimal(texto)
  if (n === null || decimales(n) > 2 || aCentimos(n) === 0n) return null
  return n
}

/**
 * El precio de una LÍNEA: como `precioValido`, pero admite cero.
 *
 * ⚠ **Es otra función a propósito, no un parámetro.** Cero es legítimo en un renglón
 * —lo que viene de regalo en la factura, el mouse con la laptop, tiene que entrar al
 * depósito—, pero `precioValido` valida también lo que se debita al pagar, y un pago
 * de cero no existe.
 */
export function precioLineaValido(texto: string): string | null {
  const n = normalizarDecimal(texto)
  if (n === null || decimales(n) > 2) return null
  return n
}

function aMillonesimas(texto: string): bigint {
  const [entero = "0", fraccion = ""] = texto.split(".")
  return BigInt(entero) * 1_000_000n + BigInt(`${fraccion}000000`.slice(0, 6))
}

/**
 * `cantidad × precio` en céntimos, redondeado como el servidor, o `null` si alguno
 * de los dos no es válido todavía —mientras se teclea—.
 */
export function subtotalCentimos(cantidad: string, precio: string): bigint | null {
  const c = cantidadValida(cantidad)
  const p = precioLineaValido(precio)
  if (c === null || p === null) return null
  // millonésimas × céntimos = céntimos × 10⁶; se divide redondeando la mitad arriba.
  return (aMillonesimas(c) * aCentimos(p) + 500_000n) / 1_000_000n
}
